import { SignedIn, SignedOut } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { Search, Plus, Lock, Users, Zap, Shield, MapPin, Globe, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PromotionSpotlight } from "@/components/PromotionSpotlight";
import { Web3CampaignBanner } from "@/components/Web3CampaignBanner";
import { CategoryGrid } from "@/components/CategoryGrid";
import { Navbar } from "@/components/Navbar";
import { HookTravelDeals } from "@/components/HookTravelDeals";
import { memo, useMemo, useCallback, useState, type FormEvent } from "react";

// Memoized feature card to prevent re-renders
interface FeatureCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  colorClass: string;
}

const FeatureCard = memo(function FeatureCard({ icon: Icon, title, description, colorClass }: FeatureCardProps) {
  return (
    <div className="glass-card p-6 h-full">
      <div className={`w-12 h-12 rounded-lg ${colorClass} flex items-center justify-center mb-4 shadow-sm`}>
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-muted-foreground">{description}</p>
    </div>
  );
});

export default function Home() {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState("");

  // Memoize features array to prevent recreation on every render
  const features = useMemo(() => [
    { icon: Zap, title: t('home.features.fast'), description: t('home.features.fastDesc'), colorClass: 'bg-primary/10 text-primary' },
    { icon: Lock, title: t('home.features.verified'), description: t('home.features.verifiedDesc'), colorClass: 'bg-accent/10 text-accent' },
    { icon: Shield, title: t('home.features.secure'), description: t('home.features.secureDesc'), colorClass: 'bg-secondary/10 text-secondary' },
    { icon: Globe, title: t('home.features.global'), description: t('home.features.globalDesc'), colorClass: 'bg-primary/10 text-primary' },
    { icon: MapPin, title: t('home.features.local'), description: t('home.features.localDesc'), colorClass: 'bg-accent/10 text-accent' },
    { icon: Users, title: t('home.features.community'), description: t('home.features.communityDesc'), colorClass: 'bg-secondary/10 text-secondary' },
  ], [t]);

  // Memoize search handler
  const handleSearchChange = useCallback((e: FormEvent<HTMLInputElement>) => {
    setSearchInput(e.currentTarget.value);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <Navbar />

      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="mb-6">
              {t('home.heroTitle')}
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              {t('home.heroSubtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button size="lg" asChild>
                <Link href="/browse">
                  <a className="flex items-center gap-2 text-primary-foreground">
                    <Search className="w-5 h-5 text-primary-foreground" />
                    {t('home.browseListings')}
                  </a>
                </Link>
              </Button>

              <SignedIn>
                <Button size="lg" variant="outline" asChild>
                  <Link href="/post">
                    <a className="flex items-center gap-2">
                      <Plus className="w-5 h-5" />
                      {t('home.postYourAd')}
                    </a>
                  </Link>
                </Button>
              </SignedIn>

              <SignedOut>
                <Button size="lg" variant="outline" asChild>
                  <Link href="/sign-up">{t('home.getStartedFree')}</Link>
                </Button>
              </SignedOut>
            </div>

            {/* Search bar preview */}
            <div className="glass-panel rounded-lg p-4 transition-all hover:shadow-glow-primary">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder={t('search.placeholder')}
                  className="flex-1 bg-transparent border-b border-input px-4 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
                />
                <Button className="shadow-lg shadow-primary/20">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding">
        <div className="container">
          <h2 className="text-center mb-12">{t('home.whyChoose')}</h2>

          <div className="grid-responsive">
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                colorClass={feature.colorClass}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Web3 Campaign Banner */}
      <Web3CampaignBanner />

      <section className="section-padding bg-muted/20">
        <div className="container">
          <PromotionSpotlight
            title="Unlock marketplace superpowers"
            description="Upgrade protections and automation so every listing stands out."
            audience={["global", "seller", "buyer"]}
            className="max-w-5xl mx-auto"
          />
        </div>
      </section>

      <HookTravelDeals />

      {/* Categories Section */}
      <section className="section-padding bg-muted/30">
        <div className="container">
          <h2 className="text-center mb-12">{t('home.browseByCategory')}</h2>
          <CategoryGrid showAll={true} />
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary/5">
        <div className="container text-center">
          <h2 className="mb-6">{t('home.readyToStart')}</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            {t('home.joinCommunity')}
          </p>

          <SignedIn>
            <Button size="lg" asChild>
              <Link href="/post">
                <a>{t('home.postFirstAd')}</a>
              </Link>
            </Button>
          </SignedIn>

          <SignedOut>
            <Button size="lg" asChild>
              <Link href="/sign-up">{t('home.signUpFree')}</Link>
            </Button>
          </SignedOut>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-background py-12">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <p className="text-xl font-bold gradient-text mb-2">{t('common.appName')}</p>
              <p className="text-muted-foreground text-sm">
                {t('common.tagline')}
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t('footer.browse')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link href="/browse"><a className="hover:text-primary">{t('footer.allListings')}</a></Link></li>
                <li><Link href="/browse"><a className="hover:text-primary">{t('footer.categories')}</a></Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t('footer.sell')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link href="/post"><a className="hover:text-primary">{t('footer.postAd')}</a></Link></li>
                <li><Link href="/dashboard"><a className="hover:text-primary">{t('footer.manageAds')}</a></Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t('footer.support')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link href="/safety-guidelines"><a className="hover:text-primary flex items-center gap-1"><Shield className="w-3 h-3" /> {t('footer.safetyGuidelines')}</a></Link></li>
                <li><Link href="/profile"><a className="hover:text-primary">{t('nav.profile')}</a></Link></li>
                <li><Link href="/dashboard"><a className="hover:text-primary">{t('nav.dashboard')}</a></Link></li>
              </ul>
            </div>
          </div>

          {/* Safety Notice */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-8">
            <div className="flex items-center gap-3 text-sm">
              <Shield className="w-5 h-5 text-primary flex-shrink-0" />
              <p className="text-muted-foreground">
                <span className="font-medium text-foreground">{t('safety.yourSafetyMatters')}</span>{" "}
                {t('safety.zeroTolerance')}
                <Link href="/safety-guidelines" className="text-primary hover:underline ml-1">
                  {t('safety.learnMore')}
                </Link>
              </p>
            </div>
          </div>

          <div className="border-t border-border pt-8 text-center text-sm text-muted-foreground">
            <p>{t('footer.copyright')}</p>
            <p className="mt-2">
              <Link href="/safety-guidelines" className="hover:text-primary">{t('footer.safety')}</Link>
              {" · "}
              <span>{t('footer.terms')}</span>
              {" · "}
              <span>{t('footer.privacy')}</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
